import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { CalendarIcon, EyeIcon, XIcon, AlertCircleIcon, ChevronDownIcon, CheckCircleIcon, AlertTriangleIcon, CreditCardIcon, WalletIcon, RepeatIcon, TrendingUpIcon, CheckIcon } from 'lucide-react';
import { mockBills, scheduleBills } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
import { getFinancingOffers } from '../mocks/financingData';
import { BRAND } from '../../../shared/config/brand';
type ScheduleType = 'due_date' | 'today' | 'next_cycle';
interface BillRowState {
  billId: string;
  paymentMethod: string;
  designatedApprover: string;
  hasError: boolean;
}
const APPROVERS = [{
  value: 'auto',
  label: 'Assign Automatically'
}, {
  value: 'Simon Verraest',
  label: 'Simon Verraest'
}, {
  value: 'John Smith',
  label: 'John Smith'
}, {
  value: 'Sarah Johnson',
  label: 'Sarah Johnson'
}];
function getSupplierPaymentMethod(supplierId: string) {
  const supplier = mockSuppliers.find(s => s.id === supplierId);
  if (!supplier) return {
    method: '',
    label: 'No payment method',
    options: []
  };
  if (supplier.payInMethod === 'card' && supplier.cardNumber) {
    const last4 = supplier.cardNumber.slice(-4);
    const brand = supplier.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard';
    return {
      method: 'card',
      label: `${brand} ending in ${last4}`,
      options: [{
        value: `card_${supplierId}`,
        label: `${brand} ending in ${last4}`
      }, {
        value: 'acquiring',
        label: 'Acquiring Balance'
      }]
    };
  }
  return {
    method: 'acquiring',
    label: 'Acquiring Balance',
    options: [{
      value: 'acquiring',
      label: 'Acquiring Balance'
    }]
  };
}
function getNextCycleDate(cycleDays: number[]): {
  date: string;
  label: string;
} | null {
  if (cycleDays.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  let nextDate: Date | null = null;
  for (const day of cycleDays) {
    let candidate: Date;
    if (day === -1) {
      // Last day of current month
      const lastDayThisMonth = new Date(currentYear, currentMonth + 1, 0);
      if (lastDayThisMonth > today) {
        candidate = lastDayThisMonth;
      } else {
        // Last day of next month
        candidate = new Date(currentYear, currentMonth + 2, 0);
      }
    } else {
      // Specific day of month
      candidate = new Date(currentYear, currentMonth, day);
      if (candidate <= today) {
        // Move to next month
        candidate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    if (!nextDate || candidate < nextDate) {
      nextDate = candidate;
    }
  }
  if (!nextDate) return null;
  return {
    date: nextDate.toISOString().split('T')[0],
    label: nextDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  };
}
function getPaymentDate(scheduleType: ScheduleType, dueDate: string, cycleDays?: number[]): string {
  if (scheduleType === 'today') {
    return new Date().toISOString().split('T')[0];
  }
  if (scheduleType === 'next_cycle' && cycleDays) {
    const nextCycle = getNextCycleDate(cycleDays);
    if (nextCycle) return nextCycle.date;
    // Fallback to today if no cycles configured
    return new Date().toISOString().split('T')[0];
  }
  // due_date: use the bill's due date, but if it's in the past, use today
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due < today) {
    return today.toISOString().split('T')[0];
  }
  return dueDate;
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
export function BillApprovalWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('due_date');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fundingMode, setFundingMode] = useState<'standard' | 'financing'>('standard');
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  // Read payment cycles from localStorage
  const paymentCycles = useMemo(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) as number[] : [];
  }, []);
  const nextCycleInfo = useMemo(() => getNextCycleDate(paymentCycles), [paymentCycles]);
  // Get selected bills
  const selectedBills = useMemo(() => {
    if (id && id !== 'schedule') {
      return mockBills.filter(b => b.id === id);
    }
    if (idsParam) {
      const ids = idsParam.split(',');
      return mockBills.filter(b => ids.includes(b.id));
    }
    return [];
  }, [id, idsParam]);
  // Per-bill row state
  const [billRows, setBillRows] = useState<BillRowState[]>([]);
  // Initialize bill row state from supplier data
  useEffect(() => {
    if (selectedBills.length > 0 && billRows.length === 0) {
      const rows = selectedBills.map(bill => {
        const supplierPay = getSupplierPaymentMethod(bill.supplierId);
        const defaultMethod = supplierPay.options.length > 0 ? supplierPay.options[0].value : '';
        return {
          billId: bill.id,
          paymentMethod: defaultMethod,
          designatedApprover: 'auto',
          hasError: !defaultMethod
        };
      });
      setBillRows(rows);
    }
  }, [selectedBills]);
  const totalAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const financingOffers = useMemo(() => getFinancingOffers(totalAmount), [totalAmount]);
  // Validation
  const allValid = fundingMode === 'financing' ? !!selectedOfferId : billRows.every(r => r.paymentMethod !== '');
  const invalidCount = billRows.filter(r => r.paymentMethod === '').length;
  // Redirect if no bills selected
  useEffect(() => {
    if (selectedBills.length === 0) {
      navigate('/bills');
    }
  }, [selectedBills, navigate]);
  const updateBillRow = (billId: string, updates: Partial<BillRowState>) => {
    setBillRows(prev => prev.map(r => {
      if (r.billId !== billId) return r;
      const updated = {
        ...r,
        ...updates
      };
      updated.hasError = !updated.paymentMethod;
      return updated;
    }));
  };
  const handleConfirmSchedule = () => {
    if (!allValid) {
      setShowValidationErrors(true);
      return;
    }
    const assignments: Record<string, {
      approver: string;
      paymentMethod: string;
      paymentDate: string;
    }> = {};
    billRows.forEach(row => {
      const bill = selectedBills.find(b => b.id === row.billId);
      if (bill) {
        assignments[row.billId] = {
          approver: row.designatedApprover,
          paymentMethod: fundingMode === 'financing' ? `financing_${selectedOfferId}` : row.paymentMethod,
          paymentDate: getPaymentDate(scheduleType, bill.dueDate, paymentCycles)
        };
      }
    });
    scheduleBills(billRows.map(r => r.billId), assignments);
    setStep(3);
  };
  // Compute which bills are assigned to "me" (Simon Verraest)
  const assignedToMe = billRows.filter(r => r.designatedApprover === 'Simon Verraest');
  const autoAssigned = billRows.filter(r => r.designatedApprover === 'auto');
  if (selectedBills.length === 0) return null;
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto" data-id="element-3488">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-3489">
        <button onClick={() => navigate('/bills')} aria-label="Close wizard" className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3490">
          <XIcon size={20} aria-hidden="true" data-id="element-3491" />
        </button>

        {/* Progress Bar */}
        <div className="flex items-center gap-3" data-id="element-3492">
          <span className="text-xs text-gray-500 font-medium" data-id="element-3493">
            {t('pages.BillApprovalWizard.step')} {step}{' '}
            {t('pages.BillApprovalWizard.of_3')}
          </span>

          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3494">
            <div className="h-full bg-[var(--brand-primary)] transition-all duration-300 ease-in-out rounded-full" style={{
            width: step === 1 ? '33%' : step === 2 ? '66%' : '100%'
          }} data-id="element-3495" />
          </div>
        </div>
        <div className="w-10" data-id="element-3496" />
      </div>

      <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6" data-id="element-3497">
        {/* ========== STEP 1: Schedule Selection ========== */}
        {step === 1 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-3xl mx-auto" data-id="element-3498">
            <div className="text-center mb-10" data-id="element-3499">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3500">
                <CalendarIcon size={24} className="text-gray-900" data-id="element-3501" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3502">
                {t('pages.BillApprovalWizard.when_would_you_like_the_payments')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3503">
                {t('pages.BillApprovalWizard.select_one_of_the_options_below')}
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-3504">
              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'due_date' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3505">
                <div className="flex items-start" data-id="element-3506">
                  <div className="flex items-center h-5" data-id="element-3507">
                    <input type="radio" name="scheduleType" value="due_date" checked={scheduleType === 'due_date'} onChange={() => setScheduleType('due_date')} className="w-4 h-4 text-[var(--brand-primary)] border-gray-300 focus:ring-[var(--brand-primary)] mt-0.5" data-id="element-3508" />
                  </div>
                  <div className="ml-4" data-id="element-3509">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3510">
                      {t('pages.BillApprovalWizard.payment_scheduled_on_invoice_due_date')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3511">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills')}
                    </span>
                  </div>
                </div>
              </label>

              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'today' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3512">
                <div className="flex items-start" data-id="element-3513">
                  <div className="flex items-center h-5" data-id="element-3514">
                    <input type="radio" name="scheduleType" value="today" checked={scheduleType === 'today'} onChange={() => setScheduleType('today')} className="w-4 h-4 text-[var(--brand-primary)] border-gray-300 focus:ring-[var(--brand-primary)] mt-0.5" data-id="element-3515" />
                  </div>
                  <div className="ml-4" data-id="element-3516">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3517">
                      {t('pages.BillApprovalWizard.payment_scheduled_today')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3518">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_2')}
                    </span>
                  </div>
                </div>
              </label>

              {paymentCycles.length > 0 && <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'next_cycle' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3519">
                  <div className="flex items-start" data-id="element-3520">
                    <div className="flex items-center h-5" data-id="element-3521">
                      <input type="radio" name="scheduleType" value="next_cycle" checked={scheduleType === 'next_cycle'} onChange={() => setScheduleType('next_cycle')} className="w-4 h-4 text-[var(--brand-primary)] border-gray-300 focus:ring-[var(--brand-primary)] mt-0.5" data-id="element-3522" />
                    </div>
                    <div className="ml-4" data-id="element-3523">
                      <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3524">
                        {t('pages.BillApprovalWizard.payment_scheduled_on_next_cycle')}
                      </span>
                      <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3525">
                        {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_3')}
                      </span>
                      {nextCycleInfo && <div className="mt-2 p-2 bg-[#F0F2F5] rounded text-xs text-gray-600" data-id="element-3526">
                          {t('pages.BillApprovalWizard.next_cycle')}{' '}
                          {nextCycleInfo.label}
                        </div>}
                    </div>
                  </div>
                </label>}
            </div>

            <div className="text-center mb-10" data-id="element-3527">
              <p className="text-xs text-gray-500" data-id="element-3528">
                {t('pages.BillApprovalWizard.define_your_payment_cycle_to_align')}{' '}
                <button className="text-[var(--brand-primary)] font-medium hover:underline" data-id="element-3529">
                  {t('pages.BillApprovalWizard.go_to_bills_settings')}
                </button>
              </p>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3530">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-[var(--brand-primary)] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3531">
                {t('pages.BillApprovalWizard.cancel')}
              </button>
              <button onClick={() => setStep(2)} className="px-8 py-2.5 text-sm font-medium text-[#1A1A1A] bg-[var(--brand-primary)] rounded-lg hover:bg-[var(--brand-primaryhover)] transition-colors shadow-sm" data-id="element-3532">
                {t('pages.BillApprovalWizard.next')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 2: Review & Confirm ========== */}
        {step === 2 && <div className="animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-3533">
            <div className="text-center mb-8" data-id="element-3534">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3535">
                <EyeIcon size={24} className="text-gray-900" data-id="element-3536" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3537">
                {t('pages.BillApprovalWizard.review_and_confirm')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3538">
                {t('pages.BillApprovalWizard.please_review_the_details_carefully_before')}
              </p>
            </div>

            {/* Info Banner */}
            <div className="bg-[#F0F2F5] rounded-xl p-5 mb-6 flex items-start" data-id="element-3539">
              <AlertCircleIcon size={20} className="text-gray-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-3540" />

              <div data-id="element-3541">
                <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-3542">
                  {t('pages.BillApprovalWizard.how_to_approve_bill_payment_schedules')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-3543">
                  {t('pages.BillApprovalWizard.after_submitting_the_bill_payment_schedules')}
                </p>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3544">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-3545">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3546">
                  {t('pages.BillApprovalWizard.summary')}
                </h3>
              </div>
              <div className="p-6" data-id="element-3547">
                <div className="grid grid-cols-3 gap-6" data-id="element-3548">
                  <div data-id="element-3549">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3550">
                      {t('pages.BillApprovalWizard.total_payments')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3551">
                      {selectedBills.length}
                    </span>
                  </div>
                  <div data-id="element-3552">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3553">
                      {t('pages.BillApprovalWizard.total_amount')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3554">
                      {t('pages.BillApprovalWizard.usd')}{' '}
                      {totalAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                    </span>
                  </div>
                  <div data-id="element-3555">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3556">
                      {t('pages.BillApprovalWizard.payment_schedule')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3557">
                      {scheduleType === 'due_date' ? 'On due date' : scheduleType === 'today' ? 'Immediately' : 'Next cycle'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Funding Mode Toggle */}
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-3558">
              <button onClick={() => setFundingMode('standard')} className={`p-4 rounded-xl border text-left transition-all ${fundingMode === 'standard' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] bg-white shadow-sm' : 'border-gray-200 bg-gray-50 hover:bg-white'}`} data-id="element-3559">
                <div className="flex items-center gap-3 mb-1" data-id="element-3560">
                  <WalletIcon size={18} className={fundingMode === 'standard' ? 'text-[var(--brand-primary)]' : 'text-gray-500'} data-id="element-3561" />
                  <span className={`font-semibold ${fundingMode === 'standard' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-3562">
                    Pay from balances
                  </span>
                </div>
                <p className="text-xs text-gray-500 ml-7" data-id="element-3563">
                  Use your existing acquiring balance or linked cards.
                </p>
              </button>

              <button onClick={() => setFundingMode('financing')} className={`p-4 rounded-xl border text-left transition-all relative overflow-hidden ${fundingMode === 'financing' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] bg-white shadow-sm' : 'border-gray-200 bg-gray-50 hover:bg-white'}`} data-id="element-3564">
                {fundingMode !== 'financing' && <div className="absolute top-0 right-0 bg-[var(--brand-primary)] text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg" data-id="element-3565">
                    RECOMMENDED
                  </div>}
                <div className="flex items-center gap-3 mb-1" data-id="element-3566">
                  <TrendingUpIcon size={18} className={fundingMode === 'financing' ? 'text-[var(--brand-primary)]' : 'text-gray-500'} data-id="element-3567" />
                  <span className={`font-semibold ${fundingMode === 'financing' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-3568">
                    Finance these bills
                  </span>
                </div>
                <p className="text-xs text-gray-500 ml-7" data-id="element-3569">
                  Free up cash flow. We pay your suppliers now, you repay over
                  time.
                </p>
              </button>
            </div>

            {fundingMode === 'financing' ? <div className="space-y-4 mb-6" data-id="element-3570">
                <div className="flex items-center justify-between mb-2" data-id="element-3571">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3572">
                    Select a financing offer
                  </h3>
                  <span className="text-xs text-gray-500" data-id="element-3573">
                    Amount to finance:{' '}
                    <strong className="text-gray-900" data-id="element-3574">
                      MYR{' '}
                      {totalAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </strong>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3575">
                  {financingOffers.map(offer => <div key={offer.id} onClick={() => setSelectedOfferId(offer.id)} className={`relative p-5 rounded-xl border cursor-pointer transition-all ${selectedOfferId === offer.id ? 'border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)] bg-white shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-3576">
                      {offer.recommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap shadow-sm" data-id="element-3577">
                          {offer.highlight.toUpperCase()}
                        </div>}
                      {!offer.recommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-100 text-gray-600 border border-gray-200 text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap" data-id="element-3578">
                          {offer.highlight.toUpperCase()}
                        </div>}

                      <div className="text-center mb-4 mt-2" data-id="element-3579">
                        <h4 className="font-bold text-gray-900 text-lg" data-id="element-3580">
                          {offer.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3581">
                          {offer.tagline}
                        </p>
                      </div>

                      <div className="space-y-3 mb-5" data-id="element-3582">
                        <div className="flex justify-between items-center text-sm" data-id="element-3583">
                          <span className="text-gray-500" data-id="element-3584">Term</span>
                          <span className="font-semibold text-gray-900" data-id="element-3585">
                            {offer.termMonths} months
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-3586">
                          <span className="text-gray-500" data-id="element-3587">Holdback</span>
                          <span className="font-semibold text-gray-900" data-id="element-3588">
                            {offer.holdbackRate}% daily
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-3589">
                          <span className="text-gray-500" data-id="element-3590">Total fees</span>
                          <span className="font-semibold text-gray-900" data-id="element-3591">
                            MYR{' '}
                            {offer.totalFees.toLocaleString('en-US', {
                      minimumFractionDigits: 0
                    })}
                          </span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-100 text-center" data-id="element-3592">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-3593">
                          Total repayable
                        </div>
                        <div className="text-xl font-bold text-[var(--brand-primary)]" data-id="element-3594">
                          MYR{' '}
                          {offer.totalRepayable.toLocaleString('en-US', {
                    minimumFractionDigits: 0
                  })}
                        </div>
                      </div>

                      {selectedOfferId === offer.id && <div className="absolute top-3 right-3 text-[var(--brand-primary)]" data-id="element-3595">
                          <CheckIcon size={20} data-id="element-3596" />
                        </div>}
                    </div>)}
                </div>

                {showValidationErrors && !selectedOfferId && <p className="text-xs text-red-500 flex items-center gap-1.5 mt-2" data-id="element-3597">
                    <AlertTriangleIcon size={14} data-id="element-3598" />
                    Please select a financing offer to continue.
                  </p>}
              </div> : <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3599">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-3600">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3601">
                    {t('pages.BillApprovalWizard.payments_to_be_scheduled')}
                  </h3>
                  {showValidationErrors && invalidCount > 0 && <span className="text-xs text-red-500 font-medium flex items-center gap-1" data-id="element-3602">
                      <AlertTriangleIcon size={12} data-id="element-3603" />
                      {invalidCount} {t('pages.BillApprovalWizard.bill')}
                      {invalidCount !== 1 ? 's' : ''}{' '}
                      {t('pages.BillApprovalWizard.missing_payment_method')}
                    </span>}
                </div>
                <div className="overflow-x-auto" data-id="element-3604">
                  <table className="w-full text-left" data-id="element-3605">
                    <thead data-id="element-3606">
                      <tr className="border-b border-gray-100" data-id="element-3607">
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3608">
                          {t('pages.BillApprovalWizard.bill_details')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3609">
                          {t('pages.BillApprovalWizard.bill_due_date')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3610">
                          {t('pages.BillApprovalWizard.payment_date')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap text-right" data-id="element-3611">
                          {t('pages.BillApprovalWizard.amount')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3612">
                          {t('pages.BillApprovalWizard.pay_bill_with')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3613">
                          {t('pages.BillApprovalWizard.designated_approver')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-3614">
                      {selectedBills.map(bill => {
                  const rowState = billRows.find(r => r.billId === bill.id);
                  const supplierPay = getSupplierPaymentMethod(bill.supplierId);
                  const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
                  const isPastDue = scheduleType === 'due_date' && new Date(bill.dueDate) < new Date();
                  const hasError = showValidationErrors && rowState?.paymentMethod === '';
                  return <tr key={bill.id} className={hasError ? 'bg-red-50/40' : ''} data-id="element-3615">
                            <td className="px-5 py-4" data-id="element-3616">
                              <div className="text-sm font-semibold text-gray-900" data-id="element-3617">
                                {bill.supplierName}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-3618">
                                {bill.billNumber}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-900 whitespace-nowrap" data-id="element-3619">
                              {formatDate(bill.dueDate)}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap" data-id="element-3620">
                              <div className="flex items-center gap-1.5" data-id="element-3621">
                                <span className="text-sm font-medium text-gray-900" data-id="element-3622">
                                  {formatDate(paymentDate)}
                                </span>
                                {isPastDue && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" data-id="element-3623">
                                    {t('pages.BillApprovalWizard.adjusted')}
                                  </span>}
                                {scheduleType === 'today' && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" data-id="element-3624">
                                    {t('pages.BillApprovalWizard.immediate')}
                                  </span>}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-right whitespace-nowrap" data-id="element-3625">
                              <span className="text-sm font-semibold text-gray-900" data-id="element-3626">
                                {bill.currency}{' '}
                                {bill.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                              </span>
                            </td>
                            <td className="px-5 py-4" data-id="element-3627">
                              <div className="relative" data-id="element-3628">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3629">
                                  {rowState?.paymentMethod?.startsWith('card') ? <CreditCardIcon size={14} className="text-gray-400" data-id="element-3630" /> : <WalletIcon size={14} className="text-gray-400" data-id="element-3631" />}
                                </div>
                                <select value={rowState?.paymentMethod || ''} onChange={e => updateBillRow(bill.id, {
                          paymentMethod: e.target.value
                        })} className={`w-full appearance-none pl-8 pr-8 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)] bg-white ${hasError ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-900'}`} data-id="element-3632">
                                  {supplierPay.options.length === 0 && <option value="" data-id="element-3633">
                                      {t('pages.BillApprovalWizard.no_payment_method_configured')}
                                    </option>}
                                  {supplierPay.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-3634">
                                      {opt.label}
                                    </option>)}
                                </select>
                                <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3635" />
                              </div>
                              {hasError && <p className="text-[10px] text-red-500 mt-1" data-id="element-3636">
                                  {t('pages.BillApprovalWizard.payment_method_required')}
                                </p>}
                            </td>
                            <td className="px-5 py-4" data-id="element-3637">
                              <div className="relative" data-id="element-3638">
                                <select value={rowState?.designatedApprover || 'auto'} onChange={e => updateBillRow(bill.id, {
                          designatedApprover: e.target.value
                        })} className="w-full appearance-none px-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)] bg-white" data-id="element-3639">
                                  {APPROVERS.map(a => <option key={a.value} value={a.value} data-id="element-3640">
                                      {a.label}
                                    </option>)}
                                </select>
                                <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3641" />
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100" data-id="element-3642">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3643">
                    {t('pages.BillApprovalWizard.after_confirming_the_payment_method_and')}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-3644">
                    {t('pages.BillApprovalWizard.your_selected_payment_schedule_is')}{' '}
                    <span className="font-semibold text-gray-700" data-id="element-3645">
                      {scheduleType === 'due_date' ? 'On the billing due date' : scheduleType === 'today' ? 'Immediately (today)' : 'On the next cycle'}
                    </span>
                  </p>
                </div>
              </div>}
            {/* Validation error */}
            {showValidationErrors && !allValid && fundingMode === 'standard' && <div className="text-center mb-6" data-id="element-3646">
                  <p className="text-xs text-red-500 flex items-center justify-center gap-1.5" data-id="element-3647">
                    <AlertTriangleIcon size={14} data-id="element-3648" />
                    {t('pages.BillApprovalWizard.unable_to_schedule_payments_please_ensure')}
                  </p>
                </div>}
            <div className="flex justify-center gap-4" data-id="element-3649">
              <button onClick={() => setStep(1)} className="px-8 py-2.5 text-sm font-medium text-[var(--brand-primary)] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3650">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleConfirmSchedule} className={`px-8 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${allValid ? 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A]' : 'bg-[var(--brand-primary)]/80 text-[#1A1A1A] hover:bg-[var(--brand-primaryhover)]'}`} data-id="element-3651">
                {t('pages.BillApprovalWizard.confirm_and_schedule')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 3: Success ========== */}
        {step === 3 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-2xl mx-auto text-center" data-id="element-3652">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3653">
              <CheckCircleIcon size={32} className="text-green-600" data-id="element-3654" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-3655">
              {t('pages.BillApprovalWizard.payments_scheduled_successfully')}
            </h1>
            <p className="text-sm text-gray-500 mb-10 max-w-md mx-auto" data-id="element-3656">
              {selectedBills.length} {t('pages.BillApprovalWizard.bill')}
              {selectedBills.length !== 1 ? 's have' : ' has'}{' '}
              {t('pages.BillApprovalWizard.been_submitted_for_approval_approvers_will')}
            </p>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-3657">
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3658">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3659">
                  {t('pages.BillApprovalWizard.total_scheduled')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3660">
                  {t('pages.BillApprovalWizard.usd')}{' '}
                  {totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2
              })}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3661">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3662">
                  {t('pages.BillApprovalWizard.assigned_to_you')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3663">
                  {assignedToMe.length} {t('pages.BillApprovalWizard.bill')}
                  {assignedToMe.length !== 1 ? 's' : ''}
                </span>
                {assignedToMe.length > 0 && <p className="text-[10px] text-[var(--brand-primary)] mt-1 font-medium" data-id="element-3664">
                    {t('pages.BillApprovalWizard.view_under_assigned_to_me_tab')}
                  </p>}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3665">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3666">
                  {t('pages.BillApprovalWizard.autoassigned')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3667">
                  {autoAssigned.length} {t('pages.BillApprovalWizard.bill')}
                  {autoAssigned.length !== 1 ? 's' : ''}
                </span>
                <p className="text-[10px] text-gray-400 mt-1" data-id="element-3668">
                  {t('pages.BillApprovalWizard.based_on_threshold_rules')}
                </p>
              </div>
            </div>

            {/* Scheduled bills detail */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10 text-left" data-id="element-3669">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-3670">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3671">
                  {t('pages.BillApprovalWizard.scheduled_payments')}
                </h3>
                {fundingMode === 'financing' && selectedOfferId && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[var(--brand-primary-light)] text-[var(--brand-primary-dark)] text-xs font-semibold" data-id="element-3672">
                    <TrendingUpIcon size={14} data-id="element-3673" />
                    Financed via{' '}
                    {financingOffers.find(o => o.id === selectedOfferId)?.name}
                  </span>}
              </div>
              <div className="divide-y divide-gray-100" data-id="element-3674">
                {selectedBills.map(bill => {
              const rowState = billRows.find(r => r.billId === bill.id);
              const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
              const approverLabel = rowState?.designatedApprover === 'auto' ? 'Auto-assigned' : rowState?.designatedApprover || 'Auto-assigned';
              return <div key={bill.id} className="px-6 py-4 flex items-center justify-between" data-id="element-3675">
                      <div className="flex items-center gap-4 min-w-0" data-id="element-3676">
                        <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3677">
                          <CheckCircleIcon size={16} className="text-green-500" data-id="element-3678" />
                        </div>
                        <div className="min-w-0" data-id="element-3679">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3680">
                            {bill.supplierName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3681">
                            {bill.billNumber}{' '}
                            {t('pages.BillApprovalWizard.payment_on')}{' '}
                            {formatDate(paymentDate)}{' '}
                            {t('pages.BillApprovalWizard.approver')}{' '}
                            {approverLabel}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-4" data-id="element-3682">
                        {bill.currency}{' '}
                        {bill.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3683">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-[#1A1A1A] bg-[var(--brand-primary)] rounded-lg hover:bg-[var(--brand-primaryhover)] transition-colors shadow-sm" data-id="element-3684">
                {t('pages.BillApprovalWizard.back_to_bills')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}